/**
 * Student Quiz List Page - Modernized
 * Displays list of quiz assignments for students with modern UI matching Assignments page
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiSearch, FiClock, FiAlertCircle, FiFileText, FiLayers, FiPlayCircle, FiCheckCircle, FiAlertTriangle } from 'react-icons/fi';
import { TbAlertCircle, TbCircleCheck, TbCircleDot, TbClockExclamation, TbEye, TbPlayerPlay, TbCalendarOff } from 'react-icons/tb';
import { PiBookOpenText, PiSortAscending } from 'react-icons/pi';
import { formatUTCToChicago } from '../../utils/datetime-comprehensive';
import quizApiService from '../../services/quizApi';
import apiService from '../../services/api';
import ExtensionRequestModal from '../../components/extensions/ExtensionRequestModal';
import styles from './StudentQuizList.module.css';

const formatHeroDate = () =>
  new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
const getQuizTypeLabel = (quizType) => {
  switch (quizType) {
    case 'final_exam': return 'Final Exam';
    case 'midterm': return 'Midterm Exam';
    case 'homework':
    default:
      return 'Homework';
  }
};

const getQuizTypeClass = (quizType) => {
  if (quizType === 'final_exam') return styles.finalExam;
  if (quizType === 'midterm') return styles.midterm;
  return styles.homework;
};

const isCompleted = (assignment) => {
  return assignment.status === 'completed' || assignment.submitted_at !== null;
};

const getCardState = (assignment) => {
  const dueDateValue = assignment.assignment_due_at || assignment.quiz_due_at;
  const dueDate = dueDateValue ? new Date(dueDateValue) : null;
  const submittedAt = assignment.submitted_at ? new Date(assignment.submitted_at) : null;
  const submitted = isCompleted(assignment);
  const now = new Date();

  if (!submitted && dueDate && dueDate < now) return 'overdue';
  if (!submitted) return 'open';
  if (submittedAt && dueDate && submittedAt > dueDate) return 'completedLate';
  return 'completedOnTime';
};

const CARD_STATE_CONFIG = {
  overdue: {
    className: styles.overdueCard,
    icon: TbAlertCircle,
    statusText: 'Overdue',
    ctaLabel: 'Start Quiz',
    ctaIcon: TbPlayerPlay,
    ctaClass: styles.overdueCta
  },
  open: {
    className: styles.openCard,
    icon: TbCircleDot,
    statusText: 'Open now',
    ctaLabel: 'Start Quiz',
    ctaIcon: TbPlayerPlay,
    ctaClass: styles.openCta
  },
  completedOnTime: {
    className: styles.completedOnTimeCard,
    icon: TbCircleCheck,
    statusText: 'Completed',
    ctaLabel: 'View Results',
    ctaIcon: TbEye,
    ctaClass: styles.resultsCta
  },
  completedLate: {
    className: styles.completedLateCard,
    icon: TbClockExclamation,
    statusText: 'Completed · submitted late',
    ctaLabel: 'View Results',
    ctaIcon: TbEye,
    ctaClass: styles.resultsCta
  }
};

function StudentQuizListCard({ assignment, startingQuizId, onStartQuiz, onViewResults, onResumeQuiz, extInfo, onRequestExtension }) {
  const state = getCardState(assignment);
  const config = CARD_STATE_CONFIG[state];
  const StatusIcon = config.icon;
  const CtaIcon = config.ctaIcon;
  const dueDate = assignment.assignment_due_at || assignment.quiz_due_at;
  const dueWithinThreeDays = dueDate && !isCompleted(assignment) && new Date(dueDate) > new Date() && new Date(dueDate).getTime() - Date.now() <= 3 * 24 * 60 * 60 * 1000;
  const submittedLate = state === 'completedLate';
  const submittedOnTime = state === 'completedOnTime';
  const isStarted = !isCompleted(assignment) && assignment.latest_attempt_id;
  const ctaLabel = isStarted ? 'Resume Quiz' : config.ctaLabel;
  const ctaAction = isCompleted(assignment)
    ? () => onViewResults(assignment.latest_attempt_id)
    : isStarted
      ? () => onResumeQuiz(assignment.latest_attempt_id)
      : () => onStartQuiz(assignment.assignment_id);
  const ctaDisabled = !isCompleted(assignment) && !isStarted && startingQuizId === assignment.assignment_id;

  const metadata = isCompleted(assignment)
    ? [
        { label: 'Due', value: formatUTCToChicago(dueDate, `MMM d '·' h:mm a`), tone: submittedLate ? styles.warningValue : undefined },
        { label: 'Submitted', value: formatUTCToChicago(assignment.submitted_at, `MMM d '·' h:mm a`), tone: submittedLate ? styles.warningValue : submittedOnTime ? styles.successValue : undefined },
        { label: 'Time Limit', value: assignment.time_limit_minutes ? `${assignment.time_limit_minutes} min` : 'No Time Limit' }
      ]
    : [
        { label: 'Due', value: dueDate ? formatUTCToChicago(dueDate, `MMM d '·' h:mm a`) : 'No due date', tone: state === 'overdue' ? styles.dangerValue : dueWithinThreeDays ? styles.warningValue : undefined },
        { label: 'Time Limit', value: assignment.time_limit_minutes ? `${assignment.time_limit_minutes} min` : 'No Time Limit' },
        { label: 'Attempts', value: `${assignment.attempts_allowed} ${assignment.attempts_allowed === 1 ? 'attempt' : 'attempts'}` }
      ];
  const inlineMetadata = isCompleted(assignment)
    ? [
        `Due ${formatUTCToChicago(dueDate, `MMM d '·' h:mm a`)}`,
        `Submitted ${formatUTCToChicago(assignment.submitted_at, `MMM d '·' h:mm a`)}`,
        assignment.time_limit_minutes ? `${assignment.time_limit_minutes} min` : 'No Time Limit'
      ]
    : [
        dueDate ? `Due ${formatUTCToChicago(dueDate, `MMM d '·' h:mm a`)}` : 'No due date',
        assignment.time_limit_minutes ? `${assignment.time_limit_minutes} min` : 'No Time Limit',
        `${assignment.attempts_allowed} ${assignment.attempts_allowed === 1 ? 'attempt' : 'attempts'}`
      ];

  const formatExpiry = (ts) => {
    if (!ts) return '';
    try {
      return new Date(ts).toLocaleString(undefined, {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit',
      });
    } catch { return ''; }
  };

  return (
    <article className={`${styles.studentQuizCard} ${config.className} ${extInfo?.submissionBlocked ? styles.blockedCard : ''}`}>
      <div className={styles.studentQuizStatusBar}>
        <StatusIcon size={14} strokeWidth={2} />
        <span>{config.statusText}</span>
        {extInfo?.courseEndedLock && (
          <span className={`${styles.studentQuizStatusBar} ${styles.courseEndedPill}`}>
            <TbCalendarOff size={12} strokeWidth={2} />
            Course Ended
          </span>
        )}
        {extInfo?.examLock && (
          <span className={`${styles.studentQuizStatusBar} ${styles.courseEndedPill}`}>
            <TbCalendarOff size={12} strokeWidth={2} />
            Exam Closed
          </span>
        )}
        {extInfo?.pending && (
          <span className={`${styles.studentQuizStatusBar} ${styles.extensionPendingPill}`}>Extension Pending</span>
        )}
        {extInfo?.denied && (
          <span className={`${styles.studentQuizStatusBar} ${styles.extensionDeniedPill}`}>Extension Denied</span>
        )}
        {extInfo?.approvedActive && (
          <span className={`${styles.studentQuizStatusBar} ${styles.extensionActivePill}`}>
            Access until {formatExpiry(extInfo.ext.access_expires_at)}
          </span>
        )}
        {extInfo?.approvedExpired && (
          <span className={`${styles.studentQuizStatusBar} ${styles.extensionExpiredPill}`}>Extension Expired</span>
        )}
      </div>
      <div className={styles.studentQuizBody}>
        <div className={styles.studentQuizTitleRow}>
          <h3 className={styles.studentQuizTitle}>{assignment.title}</h3>
          <span className={`${styles.typeChip} ${getQuizTypeClass(assignment.quiz_type)}`}>
            {getQuizTypeLabel(assignment.quiz_type)}
          </span>
        </div>
        <p className={styles.studentQuizSubtitle}>
          {assignment.course_name || 'No course assigned'}
          {(assignment.instructor_first_name || assignment.instructor_last_name) && (
            <span> · {[assignment.instructor_first_name, assignment.instructor_last_name].filter(Boolean).join(' ')}</span>
          )}
        </p>
        <div className={styles.studentQuizInlineMeta}>
          {inlineMetadata.map((item, index) => (
            <React.Fragment key={`${item}-${index}`}>
              {index > 0 && <span className={styles.studentQuizInlineDot}>·</span>}
              <span>{item}</span>
            </React.Fragment>
          ))}
        </div>
        <div className={styles.studentQuizMetaRow}>
          {metadata.map((item, index) => (
            <React.Fragment key={item.label}>
              {index > 0 && <span className={styles.studentQuizMetaDivider} aria-hidden="true" />}
              <div className={styles.studentQuizMetaItem}>
                <span className={styles.studentQuizMetaLabel}>{item.label}</span>
                <span className={`${styles.studentQuizMetaValue} ${item.tone || ''}`}>{item.value}</span>
              </div>
            </React.Fragment>
          ))}
        </div>
        {extInfo?.submissionBlocked ? (
          extInfo.canRequest ? (
            <button
              className={`${styles.studentQuizCta} ${styles.extensionRequestCta}`}
              onClick={() => onRequestExtension?.(assignment)}
              type="button"
            >
              <FiClock size={14} strokeWidth={2} />
              Request Extension
            </button>
          ) : (
            <button className={`${styles.studentQuizCta} ${styles.resultsCta}`} disabled type="button">
              <FiClock size={14} strokeWidth={2} />
              Extension Pending Review
            </button>
          )
        ) : (
          <button className={`${styles.studentQuizCta} ${config.ctaClass}`} onClick={ctaAction} disabled={ctaDisabled}>
            <CtaIcon size={14} strokeWidth={2} />
            {ctaDisabled ? 'Starting...' : ctaLabel}
          </button>
        )}
      </div>
    </article>
  );
}

function StudentQuizList() {
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [courseFilter, setCourseFilter] = useState('active');
  const [sortBy, setSortBy] = useState('posted_desc');
  const [courses, setCourses] = useState([]);
  const [startingQuizId, setStartingQuizId] = useState(null);
  const [extensionsByKey, setExtensionsByKey] = useState({});
  const [extensionTarget, setExtensionTarget] = useState(null);

  const loadMyExtensions = useCallback(async () => {
    try {
      const res = await apiService.listMyExtensions();
      if (res?.success && Array.isArray(res.requests)) {
        const map = {};
        for (const r of res.requests) {
          const key = `${r.item_type}-${r.item_id}`;
          if (!map[key]) map[key] = r;
        }
        setExtensionsByKey(map);
      }
    } catch (err) { console.warn('Failed to load extensions:', err); }
  }, []);

  useEffect(() => { loadMyExtensions(); }, [loadMyExtensions]);

  const isOverdue = (assignment) => {
    if (isCompleted(assignment)) return false;
    const dueDate = assignment.assignment_due_at || assignment.quiz_due_at;
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  const getUiStatus = (assignment) => {
    if (isCompleted(assignment)) return 'completed';
    if (isOverdue(assignment)) return 'overdue';
    return 'open';
  };

  const fetchCourses = useCallback(async () => {
    try {
      const response = await quizApiService.getCourses();
      if (response.success) {
        setCourses(response.courses || response.data || []);
      }
    } catch (err) {
      console.error('Error fetching courses:', err);
    }
  }, []);

  const fetchAssignments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await quizApiService.getStudentAssignments({ limit: 1000 });
      
      if (response.success && response.data) {
        setAssignments(response.data);
      } else {
        setError('Failed to load quiz assignments');
      }
    } catch (err) {
      console.error('Error fetching quiz assignments:', err);
      setError('Failed to load quiz assignments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAssignments();
    fetchCourses();
  }, [fetchAssignments, fetchCourses]);

  const handleStartQuiz = (assignmentId) => {
    if (startingQuizId) return;
    setStartingQuizId(assignmentId);
    navigate(`/student/assignments/${assignmentId}/warning`);
  };

  const visibleCourseIds = new Set(
    assignments
      .map((assignment) => assignment.course_id)
      .filter((courseId) => courseId !== null && courseId !== undefined)
      .map((courseId) => parseInt(courseId))
  );

  const visibleCourses = courses.filter((course) => visibleCourseIds.has(parseInt(course.id)));

  // Determine active courses
  const activeCourses = visibleCourses.filter((course) => {
    // Trust explicit activation flag from backend.
    // Some active courses may have an end_date in the past but are intentionally kept active.
    return course.is_active === true || Number(course.is_active) === 1;
  });
  const activeCourseIds = useMemo(() => new Set(
    activeCourses
      .map((c) => parseInt(c.id, 10))
      .filter((id) => !Number.isNaN(id))
  ), [activeCourses]);
  const inactiveCourses = visibleCourses.filter((c) => {
    const courseId = parseInt(c.id, 10);
    return Number.isNaN(courseId) || !activeCourseIds.has(courseId);
  });

  const filteredAssignments = assignments.filter(assignment => {
    const matchesSearch = assignment.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (assignment.course_name && assignment.course_name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const uiStatus = getUiStatus(assignment);
    const matchesFilter = activeFilter === 'all' || activeFilter === uiStatus;
    
    // Course filtering logic
    let matchesCourse = false;
    if (courseFilter === 'active') {
      // Show only quizzes from active courses
      matchesCourse = assignment.course_id && activeCourseIds.has(parseInt(assignment.course_id));
    } else if (courseFilter === 'all') {
      // Show all quizzes (both active and inactive courses)
      matchesCourse = true;
    } else {
      // Show quizzes from a specific course (active or inactive)
      matchesCourse = courseFilter && assignment.course_id && parseInt(assignment.course_id) === parseInt(courseFilter);
    }
    
    return matchesSearch && matchesFilter && matchesCourse;
  }).sort((a, b) => {
    if (sortBy === 'posted_asc' || sortBy === 'posted_desc') {
      const dateA = new Date(a.assigned_at || a.created_at || 0).getTime();
      const dateB = new Date(b.assigned_at || b.created_at || 0).getTime();
      return sortBy === 'posted_asc' ? dateA - dateB : dateB - dateA;
    }

    if (sortBy === 'due_asc' || sortBy === 'due_desc') {
      const dueA = a.assignment_due_at || a.quiz_due_at;
      const dueB = b.assignment_due_at || b.quiz_due_at;
      const dateA = dueA ? new Date(dueA).getTime() : Number.MAX_SAFE_INTEGER;
      const dateB = dueB ? new Date(dueB).getTime() : Number.MAX_SAFE_INTEGER;
      return sortBy === 'due_asc' ? dateA - dateB : dateB - dateA;
    }

    if (sortBy === 'title_desc') {
      return (b.title || '').localeCompare(a.title || '', undefined, { sensitivity: 'base' });
    }

    return (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' });
  });

  // Filter counts should respect the course filter selection
  const assignmentsForCounts = assignments.filter(assignment => {
    if (courseFilter === 'active') {
      return assignment.course_id && activeCourseIds.has(parseInt(assignment.course_id));
    } else if (courseFilter === 'all') {
      return true;
    } else {
      return courseFilter && assignment.course_id && parseInt(assignment.course_id) === parseInt(courseFilter);
    }
  });

  const filterCounts = {
    all: assignmentsForCounts.length,
    open: assignmentsForCounts.filter(a => getUiStatus(a) === 'open').length,
    completed: assignmentsForCounts.filter(a => getUiStatus(a) === 'completed').length,
    overdue: assignmentsForCounts.filter(a => getUiStatus(a) === 'overdue').length,
  };

  const getExtensionInfo = useCallback((assignment) => {
    const courseActive = activeCourseIds.has(parseInt(assignment.course_id));
    const quizId = assignment.quiz_id; // extensions are scoped to quiz_id
    const ext = quizId ? extensionsByKey[`quiz-${quizId}`] || null : null;
    const expiresMs = ext?.access_expires_at ? new Date(ext.access_expires_at).getTime() : 0;
    const now = Date.now();
    const approvedActive = ext?.status === 'approved' && expiresMs > now;
    const approvedExpired = ext?.status === 'approved' && expiresMs > 0 && expiresMs <= now;

    // Exam-past-due gate: Final/Midterm quizzes lock once their due date has
    // passed, regardless of whether the course is still active.
    const isExam = ['final_exam', 'midterm'].includes(assignment.quiz_type);
    const dueDate = assignment.assignment_due_at || assignment.quiz_due_at;
    const dueMs = dueDate ? new Date(dueDate).getTime() : 0;
    const examPastDue = isExam && dueMs > 0 && dueMs < now && !isCompleted(assignment);

    const courseEndedLock = !courseActive;
    const examLock = courseActive && examPastDue;
    const anyLock = courseEndedLock || examLock;

    return {
      ext,
      courseActive,
      approvedActive,
      approvedExpired,
      pending: ext?.status === 'pending',
      denied: ext?.status === 'denied',
      courseEndedLock,
      examLock,                                                      // active course but exam past due
      submissionBlocked: anyLock && !approvedActive,
      canRequest: anyLock && !approvedActive && ext?.status !== 'pending',
    };
  }, [activeCourseIds, extensionsByKey]);

  if (loading) {
    return (
      <div className={styles.container}>
        <header className={styles.hero}>
          <div className={styles.heroEyebrow}>{formatHeroDate()}</div>
          <h1 className={styles.heroTitle}>
            My Quizzes <span className={styles.heroAmp} aria-hidden="true">&amp;</span><span className="sr-only"> and </span> Exams
          </h1>
        </header>
        <div className={styles.loadingState}>
          <FiClock size={48} />
          <h3>Loading quizzes...</h3>
          <p>Please wait while we fetch your quiz assignments.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <header className={styles.hero}>
          <div className={styles.heroEyebrow}>{formatHeroDate()}</div>
          <h1 className={styles.heroTitle}>
            My Quizzes <span className={styles.heroAmp} aria-hidden="true">&amp;</span><span className="sr-only"> and </span> Exams
          </h1>
        </header>
        <div className={styles.errorState}>
          <FiAlertCircle size={48} />
          <h3>Error loading quizzes</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const statCards = [
    { key: 'all',       label: 'All quizzes',  caption: 'Across selected courses',   icon: <FiLayers size={14} />,        accent: 'gold' },
    { key: 'open',      label: 'Open',         caption: 'Ready to take',             icon: <FiPlayCircle size={14} />,    accent: 'teal' },
    { key: 'completed', label: 'Completed',    caption: 'Submitted attempts',        icon: <FiCheckCircle size={14} />,   accent: 'sage' },
    { key: 'overdue',   label: 'Overdue',      caption: 'Needs attention',           icon: <FiAlertTriangle size={14} />, accent: 'terracotta' },
  ];

  return (
    <div className={styles.container}>
      <header className={styles.hero}>
        <div className={styles.heroEyebrow}>{formatHeroDate()}</div>
        <h1 className={styles.heroTitle}>
          My Quizzes <span className={styles.heroAmp} aria-hidden="true">&amp;</span><span className="sr-only"> and </span> Exams
        </h1>
        <p className={styles.heroSub}>
          Track open, completed, and overdue assessments across your enrolled courses.
        </p>
      </header>

      <div className={styles.filterBlock}>
        {/* Search and Filter Row */}
        <div className={styles.searchFilterRow}>
          <div className={styles.searchBar}>
            <FiSearch className={styles.searchIcon} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search quizzes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className={styles.courseFilterContainer}>
            <label className={styles.courseFilterLabel} htmlFor="student-course-filter">Course</label>
            <PiBookOpenText className={styles.filterControlIcon} />
            <select
              id="student-course-filter"
              className={styles.courseFilterSelect}
              value={courseFilter}
              onChange={(e) => setCourseFilter(e.target.value)}
            >
              <option value="active">Active Courses</option>
              {activeCourses.length === 0 && inactiveCourses.length === 0 && (
                <option value="" disabled>No courses available</option>
              )}
              {activeCourses.length > 0 && activeCourses.map(course => (
                <option key={course.id} value={course.id}>
                  {course.code} - {course.name}
                </option>
              ))}
              {inactiveCourses.length > 0 && (
                <>
                  <option disabled>──────────</option>
                  <option disabled>Past Courses</option>
                  {inactiveCourses.map(course => (
                    <option key={course.id} value={course.id}>
                      {course.code} - {course.name}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>

          <div className={styles.courseFilterContainer}>
            <label className={styles.courseFilterLabel} htmlFor="student-sort-filter">Sort</label>
            <PiSortAscending className={styles.filterControlIcon} />
            <select
              id="student-sort-filter"
              className={styles.courseFilterSelect}
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="posted_desc">Recently updated</option>
              <option value="posted_asc">Oldest Posted</option>
              <option value="due_asc">Due Soonest</option>
              <option value="due_desc">Due Latest</option>
              <option value="title_asc">Title A-Z</option>
              <option value="title_desc">Title Z-A</option>
            </select>
          </div>
        </div>

      </div>

      <div className={styles.statGrid} role="tablist" aria-label="Filter by status">
        {statCards.map((s, i) => (
          <motion.button
            key={s.key}
            type="button"
            role="tab"
            aria-selected={activeFilter === s.key}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: i * 0.04 }}
            className={`${styles.statCard} ${styles[`accent_${s.accent}`]} ${activeFilter === s.key ? styles.statActive : ''}`}
            onClick={() => setActiveFilter(s.key)}
          >
            <span className={styles.statLabel}>
              <span className={styles.statSwatch}>{s.icon}</span>
              {s.label}
            </span>
            <span className={styles.statValue}>{filterCounts[s.key]}</span>
            <span className={styles.statCaption}>{s.caption}</span>
          </motion.button>
        ))}
      </div>

{filteredAssignments.length > 0 ? (
  <div className={styles.quizList}>
    {filteredAssignments.map((assignment, index) => (
      <motion.div
        key={assignment.assignment_id}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: index * 0.05 }}
      >
        <StudentQuizListCard
          assignment={assignment}
          startingQuizId={startingQuizId}
          onStartQuiz={handleStartQuiz}
          onResumeQuiz={(attemptId) => navigate(`/student/attempt/${attemptId}`)}
          onViewResults={(attemptId) => navigate(`/student/result/${attemptId}`)}
          extInfo={getExtensionInfo(assignment)}
          onRequestExtension={(a) => setExtensionTarget({
            itemType: 'quiz',
            itemId: a.quiz_id,
            itemTitle: a.title,
            courseName: a.course_name,
          })}
        />
      </motion.div>
    ))}
  </div>
      ) : (
        <div className={styles.emptyState}>
          <FiFileText />
          <h3>No quizzes found</h3>
          <p>
            {searchTerm || activeFilter !== 'all' || courseFilter !== 'active'
              ? 'Try adjusting your search or filters to find what you\'re looking for.'
              : 'You don\'t have any quiz assignments yet.'
            }
          </p>
        </div>
      )}

      <ExtensionRequestModal
        isOpen={!!extensionTarget}
        onClose={() => setExtensionTarget(null)}
        onSubmitted={loadMyExtensions}
        itemType={extensionTarget?.itemType}
        itemId={extensionTarget?.itemId}
        itemTitle={extensionTarget?.itemTitle}
        courseName={extensionTarget?.courseName}
      />
    </div>
  );
}

export default StudentQuizList;
